import { CheckCircleOutlined, ReloadOutlined, WalletOutlined } from '@ant-design/icons';
import { Alert, App as AntApp, Button, Checkbox, Descriptions, Drawer, Input, Modal, Radio, Result, Space, Spin, Steps, Tag, Typography } from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';

import type { BookingConfirmation, BookingTotal, BookingType, DayResult, JobFlight, OfferRefreshResult, SearchParams, WalletBalance } from '../../../src/types.ts';
import { api, ApiRequestError, errorText } from '../lib/api.ts';
import { fmtMoney, fmtPrice } from '../lib/format.ts';
import { FlightCard } from './FlightCard.tsx';
import { PassengerPicker } from './PassengerPicker.tsx';

export interface BookingTarget {
  flight: JobFlight;
  day: DayResult;
  params: SearchParams;
}

interface Props {
  target: BookingTarget | null;
  wallet: WalletBalance | null;
  onClose: () => void;
  /** Called after a confirmed booking so the app can refresh the wallet and show the trip. */
  onBooked: (result: BookingConfirmation) => void;
  onAddFunds: () => void;
  onRequiresLogin: () => void;
}

const feeLabel = (f: { description?: string; descriptionEn?: string; descriptionAr?: string; [k: string]: unknown }): string =>
  String(f.descriptionEn || f.description || f.descriptionAr || f.name || f.type || 'Fee');

interface PaxFare {
  fare?: number;
  tax?: number;
  extraWeight?: number;
  seatSelection?: number;
  cashback?: number;
  retailMarkup?: number;
  total?: number;
  currency?: string;
  passengerDTO?: { firstName?: string; lastName?: string; passengerType?: string } | null;
}

/** etravel prices each traveller separately; show fare + tax per person when it does. */
function paxFares(total: BookingTotal): PaxFare[] {
  const v = total.passengerFareDetails;
  return Array.isArray(v) ? (v as PaxFare[]) : [];
}

/**
 * Book a fare found in a month search: refresh its price, pick travellers,
 * review etravel's quote, confirm. Mirrors the etravel website's own steps —
 * the same three endpoints, in the same order, with the same payloads.
 */
export function BookingDrawer({ target, wallet, onClose, onBooked, onAddFunds, onRequiresLogin }: Props) {
  const { message } = AntApp.useApp();
  const [step, setStep] = useState(0);
  const [offer, setOffer] = useState<OfferRefreshResult | null>(null);
  const [offerError, setOfferError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [bookingType, setBookingType] = useState<BookingType>('FINAL');
  const [openReturn, setOpenReturn] = useState(false);
  const [promo, setPromo] = useState('');
  const [passengerIds, setPassengerIds] = useState<number[]>([]);
  const [total, setTotal] = useState<BookingTotal | null>(null);
  const [quoting, setQuoting] = useState(false);
  const [rules, setRules] = useState<string | null>(null);
  const [rulesOpen, setRulesOpen] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [done, setDone] = useState<BookingConfirmation | null>(null);
  const [now, setNow] = useState(Date.now());

  const need = useMemo(
    () => ({ adults: target?.params.adults ?? 1, children: target?.params.children ?? 0, infants: target?.params.infants ?? 0 }),
    [target],
  );

  const refresh = useCallback(async () => {
    if (!target) return;
    const { flight, day, params } = target;
    setRefreshing(true);
    setOfferError(null);
    setTotal(null);
    setAccepted(false);
    try {
      const r = await api.refreshOffer({
        provider: flight.provider,
        origin: flight.originCode,
        destination: flight.destinationCode,
        departureDate: day.date,
        returnDate: flight.returnDate,
        cabinClass: String(params.cabinClass),
        adults: params.adults,
        children: params.children,
        infants: params.infants,
        flightNos: flight.segments.map((s) => s.flightNo ?? ''),
        departures: flight.segments.map((s) => s.dep ?? ''),
        previousPrice: flight.price,
      });
      setOffer(r);
    } catch (err) {
      if (err instanceof ApiRequestError && err.requiresLogin) onRequiresLogin();
      setOfferError(errorText(err));
    } finally {
      setRefreshing(false);
    }
  }, [target, onRequiresLogin]);

  // Reset everything when a new fare is opened.
  useEffect(() => {
    setStep(0);
    setOffer(null);
    setOfferError(null);
    setBookingType('FINAL');
    setOpenReturn(false);
    setPromo('');
    setPassengerIds([]);
    setTotal(null);
    setRules(null);
    setAccepted(false);
    setDone(null);
    if (target) void refresh();
  }, [target, refresh]);

  // Tick for the quote-validity countdown.
  useEffect(() => {
    if (!offer || done) return;
    const t = setInterval(() => setNow(Date.now()), 15_000);
    return () => clearInterval(t);
  }, [offer, done]);

  const msLeft = offer ? Date.parse(offer.expiresAt) - now : 0;
  const expired = Boolean(offer) && msLeft <= 0;

  const bookingBody = () =>
    offer
      ? {
          offerId: offer.offerId,
          passengerIds,
          bookingType,
          promoCode: promo.trim() || undefined,
          preBookId: total?.preBookId ?? undefined,
          openReturn: bookingType === 'FINAL' && openReturn,
        }
      : null;

  const quote = async () => {
    const body = bookingBody();
    if (!body) return;
    setQuoting(true);
    try {
      const t = await api.bookingTotal(body);
      setTotal(t);
      setStep(2);
    } catch (err) {
      if (err instanceof ApiRequestError && err.requiresLogin) onRequiresLogin();
      else if (err instanceof ApiRequestError && err.status === 410) {
        message.warning('The fare quote expired — refreshing the price.');
        setStep(0);
        void refresh();
      } else message.error(errorText(err), 8);
    } finally {
      setQuoting(false);
    }
  };

  const showRules = async () => {
    setRulesOpen(true);
    if (rules != null) return;
    const body = bookingBody();
    if (!body) return;
    try {
      const r = await api.bookingRules(body);
      setRules(String(r?.rules ?? '') || 'The provider returned no fare rules.');
    } catch (err) {
      setRules(`Could not load fare rules: ${errorText(err)}`);
    }
  };

  const confirm = async () => {
    const body = bookingBody();
    if (!body) return;
    setConfirming(true);
    try {
      const r = await api.confirmBooking(body);
      setDone(r);
      onBooked(r);
    } catch (err) {
      if (err instanceof ApiRequestError && err.requiresLogin) onRequiresLogin();
      else message.error(errorText(err), 10);
    } finally {
      setConfirming(false);
    }
  };

  const price = offer?.flight.price ?? target?.flight.price ?? 0;
  const drift = offer?.previousPrice != null ? price - offer.previousPrice : 0;
  const balance = wallet?.balance ?? null;
  const shortBy = total && balance != null && bookingType === 'FINAL' ? total.total - balance : 0;
  const isRoundTrip = Boolean(target?.flight.returnDate);
  const canOpenReturn = isRoundTrip && /VIDECOM/i.test(offer?.flight.gdsType ?? target?.flight.gdsType ?? '');

  return (
    <Drawer
      open={target !== null}
      onClose={onClose}
      size={720}
      className="booking-drawer"
      title={
        <Space>
          <span>Book flight</span>
          {target && (
            <Typography.Text type="secondary" style={{ fontWeight: 400 }}>
              {target.flight.originCode} {isRoundTrip ? '⇆' : '→'} {target.flight.destinationCode} · {target.day.date}
              {target.flight.returnDate ? ` – ${target.flight.returnDate}` : ''}
            </Typography.Text>
          )}
        </Space>
      }
    >
      {done ? (
        <Result
          status="success"
          icon={<CheckCircleOutlined />}
          title={bookingType === 'INITIAL' ? 'Seats held' : 'Flight booked'}
          subTitle={
            <div>
              {done.pnr && (
                <div>
                  PNR <Typography.Text strong copyable>{String(done.pnr)}</Typography.Text>
                </div>
              )}
              {Array.isArray(done.flightBookingIds) && done.flightBookingIds.length > 0 && (
                <div>Booking reference{done.flightBookingIds.length > 1 ? 's' : ''}: {done.flightBookingIds.join(', ')}</div>
              )}
              {done.total != null && <div>Charged {fmtPrice(Number(done.total), done.currency ?? total?.currency)}</div>}
              <div style={{ marginTop: 8 }}>
                {bookingType === 'INITIAL'
                  ? 'The hold expires at the airline’s deadline — confirm it from Bookings before then.'
                  : 'The e-ticket is sent to your etravel email. Find it under Bookings any time.'}
              </div>
            </div>
          }
          extra={
            <Button type="primary" onClick={onClose}>
              Done
            </Button>
          }
        />
      ) : (
        <>
          <Steps
            size="small"
            current={step}
            style={{ marginBottom: 20 }}
            items={[{ title: 'Live price' }, { title: 'Travellers' }, { title: 'Review & pay' }]}
          />

          {step === 0 && (
            <Space orientation="vertical" size={12} style={{ width: '100%' }}>
              {refreshing && (
                <div className="booking-loading">
                  <Spin /> <span>Checking the current price with {target?.flight.provider}…</span>
                </div>
              )}
              {offerError && (
                <Alert
                  type="warning"
                  showIcon
                  message="This fare could not be re-priced"
                  description={offerError}
                  action={
                    <Button size="small" icon={<ReloadOutlined />} onClick={() => void refresh()}>
                      Try again
                    </Button>
                  }
                />
              )}
              {offer && (
                <>
                  {drift !== 0 ? (
                    <Alert
                      type={drift > 0 ? 'warning' : 'success'}
                      showIcon
                      message={
                        drift > 0
                          ? `Price went up by ${fmtMoney(drift)} ${offer.flight.currency} since your search (${fmtMoney(offer.previousPrice)} → ${fmtMoney(price)})`
                          : `Price dropped by ${fmtMoney(-drift)} ${offer.flight.currency} since your search`
                      }
                    />
                  ) : (
                    <Alert type="success" showIcon message={`Price confirmed: ${fmtPrice(price, offer.flight.currency)} — unchanged since your search.`} />
                  )}
                  <FlightCard flight={{ ...offer.flight, originCode: target!.flight.originCode, destinationCode: target!.flight.destinationCode, returnDate: target!.flight.returnDate }} defaultOpen plain />
                  <div className="booking-nav">
                    <Typography.Text type="secondary">
                      Quote valid for {Math.max(1, Math.round(msLeft / 60_000))} min · {offer.offersSeen} fare{offer.offersSeen === 1 ? '' : 's'} seen
                    </Typography.Text>
                    <Button type="primary" onClick={() => setStep(1)} disabled={expired}>
                      Choose travellers
                    </Button>
                  </div>
                </>
              )}
            </Space>
          )}

          {step === 1 && offer && (
            <Space orientation="vertical" size={16} style={{ width: '100%' }}>
              <div>
                <Typography.Text strong>Booking type</Typography.Text>
                <Radio.Group
                  style={{ display: 'block', marginTop: 6 }}
                  value={bookingType}
                  onChange={(e) => {
                    setBookingType(e.target.value as BookingType);
                    if (e.target.value === 'INITIAL') setOpenReturn(false);
                  }}
                  options={[
                    { value: 'FINAL', label: 'Book & pay now — issue the ticket immediately from the wallet' },
                    { value: 'INITIAL', label: 'Hold seats — reserve now, pay before the airline’s deadline' },
                  ]}
                />
                {canOpenReturn && (
                  <Checkbox style={{ marginTop: 8 }} checked={openReturn} disabled={bookingType !== 'FINAL'} onChange={(e) => setOpenReturn(e.target.checked)}>
                    Open return date (VIDECOM fares only)
                  </Checkbox>
                )}
              </div>
              <div>
                <Typography.Text strong>Travellers</Typography.Text>
                <PassengerPicker need={need} value={passengerIds} onChange={setPassengerIds} />
              </div>
              <div>
                <Typography.Text strong>Promo code</Typography.Text>
                <Input placeholder="Optional" value={promo} onChange={(e) => setPromo(e.target.value)} style={{ maxWidth: 260, display: 'block', marginTop: 6 }} />
              </div>
              <div className="booking-nav">
                <Button onClick={() => setStep(0)}>Back</Button>
                <Button
                  type="primary"
                  loading={quoting}
                  disabled={expired || passengerIds.length !== need.adults + need.children + need.infants}
                  onClick={() => void quote()}
                >
                  Get the total
                </Button>
              </div>
            </Space>
          )}

          {step === 2 && offer && total && (
            <Space orientation="vertical" size={16} style={{ width: '100%' }}>
              <FlightCard flight={{ ...offer.flight, originCode: target!.flight.originCode, destinationCode: target!.flight.destinationCode, returnDate: target!.flight.returnDate }} plain />
              <Descriptions
                size="small"
                bordered
                column={1}
                title="Payment details"
                items={[
                  ...paxFares(total).map((p, i) => ({
                    key: `p${i}`,
                    label: `${p.passengerDTO?.firstName ?? 'Traveller'} ${p.passengerDTO?.lastName ?? ''} ${p.passengerDTO?.passengerType ? `(${p.passengerDTO.passengerType})` : ''}`.trim(),
                    children: (
                      <span>
                        {fmtPrice(p.total ?? null, p.currency ?? total.currency)}
                        <Typography.Text type="secondary" style={{ marginInlineStart: 8, fontSize: 12 }}>
                          fare {fmtMoney(p.fare)} + tax {fmtMoney(p.tax)}
                          {p.extraWeight ? ` + bags ${fmtMoney(p.extraWeight)}` : ''}
                          {p.seatSelection ? ` + seats ${fmtMoney(p.seatSelection)}` : ''}
                          {p.cashback ? ` − cashback ${fmtMoney(p.cashback)}` : ''}
                        </Typography.Text>
                      </span>
                    ),
                  })),
                  ...(total.fees ?? []).map((f, i) => ({ key: `f${i}`, label: feeLabel(f), children: fmtPrice(Number(f.amount), f.currency) })),
                  { key: 'type', label: 'Booking', children: bookingType === 'INITIAL' ? 'Hold (pay later)' : 'Pay now' },
                  { key: 'pax', label: 'Travellers', children: `${passengerIds.length}` },
                  {
                    key: 'total',
                    label: <strong>Total</strong>,
                    children: <strong style={{ fontSize: 18, color: '#302b78' }}>{fmtPrice(total.total, total.currency)}</strong>,
                  },
                ]}
              />
              {balance != null && (
                <Alert
                  type={shortBy > 0 ? 'error' : 'info'}
                  showIcon
                  icon={<WalletOutlined />}
                  message={
                    shortBy > 0
                      ? `Wallet balance ${fmtPrice(balance, wallet?.currency)} is short by ${fmtPrice(shortBy, total.currency)}.`
                      : `Wallet balance ${fmtPrice(balance, wallet?.currency)}${bookingType === 'FINAL' ? ` → ${fmtPrice(balance - total.total, wallet?.currency)} after booking` : ''}.`
                  }
                  action={
                    shortBy > 0 ? (
                      <Button size="small" type="primary" onClick={onAddFunds}>
                        Add funds
                      </Button>
                    ) : undefined
                  }
                />
              )}
              <Checkbox checked={accepted} onChange={(e) => setAccepted(e.target.checked)}>
                I accept the{' '}
                <a onClick={(e) => { e.preventDefault(); void showRules(); }} href="#rules">
                  fare rules
                </a>{' '}
                and the airline's conditions.
              </Checkbox>
              <div className="booking-nav">
                <Button onClick={() => setStep(1)}>Back</Button>
                <Space>
                  <Tag bordered={false}>Quote valid {Math.max(0, Math.round(msLeft / 60_000))} min</Tag>
                  <Button type="primary" size="large" loading={confirming} disabled={!accepted || expired || shortBy > 0} onClick={() => void confirm()}>
                    {bookingType === 'INITIAL' ? 'Hold seats' : `Pay ${fmtPrice(total.total, total.currency)}`}
                  </Button>
                </Space>
              </div>
            </Space>
          )}

          {expired && (
            <Alert
              style={{ marginTop: 16 }}
              type="warning"
              showIcon
              message="This price quote has expired."
              action={
                <Button size="small" onClick={() => { setStep(0); void refresh(); }}>
                  Refresh price
                </Button>
              }
            />
          )}
        </>
      )}

      <Modal open={rulesOpen} onCancel={() => setRulesOpen(false)} footer={null} title="Fare rules" width={760}>
        {rules == null ? <Spin /> : <pre className="rules">{rules}</pre>}
      </Modal>
    </Drawer>
  );
}
